import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { ProductCard } from '@noths/polaris-design-system-candidate-components';

import type { PartnerImage, PartnerProfileProps } from './types';
import * as styles from './PartnerProfile.styles';

/**
 * How much of the viewport the portrait occupies at each width. `sizes` is a hint
 * about the viewport rather than a layout decision, so it stays a literal string:
 * the widths match those the profile's own container queries reflow at, and are the
 * same values this component asked for before the migration, so the browser picks
 * the same file from `srcset` as it did then.
 */
const IMAGE_SIZES =
  '(min-width: 1440px) 330px, (min-width: 1024px) 25vw, (min-width: 768px) 33vw, 75vw';

const buildSrcSet = (images: PartnerImage[]) =>
  images.map(({ src, width }) => `${src} ${width}w`).join(', ');

/**
 * One partner within the block. Layout-agnostic: it reflows against its own inline
 * size (see the styles) rather than the viewport, so it sits equally well one or two
 * to a row.
 */
export const PartnerProfile = ({
  copy,
  cta,
  heading,
  imageHeight,
  imageWidth,
  images,
  lazyImages,
  onPartnerClick,
  onProductClick,
  onVisible = () => {},
  products,
  url,
}: PartnerProfileProps) => {
  const productsRef = useImpressionRef<HTMLDivElement>(() => onVisible());

  return (
    <div css={styles.partner}>
      <div css={styles.partnerLayout}>
        {/* Empty alt: the portrait illustrates the copy beside it, which already
            names the partner, so describing it again would announce them twice. */}
        <img
          alt=""
          css={styles.partnerImage}
          data-testid="partner-portrait"
          height={imageHeight}
          loading={lazyImages ? 'lazy' : 'eager'}
          sizes={IMAGE_SIZES}
          src={images[0]?.src}
          srcSet={buildSrcSet(images)}
          width={imageWidth}
        />
        <div css={styles.partnerCopy}>
          <h3 css={styles.partnerHeading}>{heading}</h3>
          <p css={styles.partnerBody}>{copy}</p>
          <a css={styles.partnerCta} href={url} onClick={onPartnerClick}>
            {cta}
          </a>
        </div>
        <div css={styles.products} ref={productsRef}>
          {products.map((product, productIndex) => (
            /* Keyed on the product code, not the title: a partner showing two
               differently-priced versions of the same product would otherwise
               collapse into one card. */
            <ProductCard
              key={product.product.code}
              {...product}
              onProductClick={(e) => onProductClick?.(e, { productIndex })}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

PartnerProfile.displayName = 'PartnerProfile';
